import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PrimaryButtonFitContainer } from "../../../../components/Buttons/Buttons";
import { SelectBoxV2 } from "../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../components/TextFields/TextFields";
import { RichEditor } from "../../../../components/RichEditor/RichEditor";
import type {
  CreateAlertPayload,
  OptionType,
  SystemMessageKind,
} from "../../../../types/types";
import { Form } from "../../../../layout/FormLayout";
import { useFormWrapper } from "../../../../util/util";
import { DatePicker } from "../../../../components/DatePicker/DatePicker";
import { ToggleSwitchV2 } from "../../../../components/ToggleSwitch/ToggleSwitch";
import moment from "moment";
import { strings } from "../../../../util/strings";
import { useEffect } from "react";
import styled from "styled-components";

// Define styled wrapper for this specific instance
const BroadcastEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 150px;

  .quill {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ql-toolbar {
    flex-shrink: 0;
  }

  .ql-container {
    flex-grow: 1;
    overflow: hidden;
    display: flex;
  }

  .ql-editor {
    height: calc(100% - 42px) !important;
    overflow-y: auto !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

type CreateBroadcastFormValues = {
  type: OptionType<SystemMessageKind>;
  header: string;
  content: string;
  expires_at: string | null;
  publish_to_buyers: boolean;
};

type CreateBroadcastFormProps = {
  onSubmit: (data: CreateAlertPayload) => Promise<void>;
  isSubmitting: boolean;
};

export const CreateBroadcastForm = ({
  onSubmit,
  isSubmitting,
}: CreateBroadcastFormProps) => {
  const { t } = useTranslation();

  const typeOptions: OptionType<SystemMessageKind>[] = [
    { label: t("Info"), value: "info" },
    { label: t("Alert"), value: "alert" },
  ];

  const initialTypeOption = typeOptions[0];

  const methodsOfUseform = useFormWrapper<CreateBroadcastFormValues>({});

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    formState,
    setValue,
    watch,
  } = methodsOfUseform;

  useEffect(() => {
    register("content", { required: strings(t).thisIsARequiredField });
  }, [register, t]);

  const handleFormSubmit = async (data: CreateBroadcastFormValues) => {
    const payload: CreateAlertPayload = {
      type: data.type.value,
      header: data.header,
      content: data.content,
      expires_at: data.expires_at === "" ? null : data.expires_at,
      publish_to_buyers: data.publish_to_buyers,
    };
    await onSubmit(payload);
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <Controller
        placeholder={t("Select Type")}
        name="type"
        control={control}
        options={typeOptions}
        rules={{ required: strings(t).thisIsARequiredField }}
        errors={errors}
        formState={formState}
        as={SelectBoxV2}
        defaultValue={initialTypeOption}
      />

      <TextField
        label={t("Header")}
        theref={register({
          required: true,
          pattern: {
            value: /\S/,
            message: t("Header cannot be empty"),
          },
        })}
        name="header"
        formState={formState}
        errors={errors}
        type="text"
        required
      />

      <BroadcastEditorWrapper>
        <RichEditor
          value={""}
          name="content"
          label={t("Content")}
          placeholder={t("Broadcast message content")}
          useFormMethods={{ errors, register, setValue, watch, formState }}
        />
      </BroadcastEditorWrapper>

      <DatePicker
        label={t("Expires On (Optional)")}
        name="expires_at"
        isOutsideRange={(day) => day.isSameOrBefore(moment.utc())}
        methodsOfUseForm={methodsOfUseform}
      />
      <ToggleSwitchV2
        label={t("Publish to Customers")}
        name="publish_to_buyers"
        ref={register}
        defaultChecked={false}
      />

      <PrimaryButtonFitContainer type="submit" disabled={isSubmitting}>
        {t("Create")}
      </PrimaryButtonFitContainer>
    </Form>
  );
};
